import firebase from "firebase/app";

import { useEffect, useMemo, useState } from "react";

import secondsToDuration from "astrid-firestore/src/api/articles/utils/secondsToDuration";
import getAcceptedArtifactFiles from "astrid-firestore/src/api/artifacts/getAcceptedArtifactFiles";
import updateProduction from "astrid-firestore/src/api/productions/updateProduction";

export default function useProductionArtifactManager({ articles, artifacts, production }) {
	const [articleArtifactMap, setArticleArtifactMap] = useState(
		production.articleIds.reduce(
			(acc, articleId) => ({
				...acc,
				[articleId]: production.articles?.[articleId]?.artifactIds?.[0] || null,
			}),
			{},
		),
	);

	const updateArticlesMap = (articleArtifactMap) => {
		setArticleArtifactMap(articleArtifactMap);

		return updateProduction(firebase, {
			ref: production.ref,
			articles: Object.entries(articleArtifactMap).reduce(
				(acc, [articleId, artifactId]) => ({
					...acc,
					[articleId]: {
						artifactIds: artifactId ? [artifactId] : [],
					},
				}),
				{},
			),
		});
	};

	useEffect(() => {
		if (!production.articles && artifacts?.length > 0) {
			setArticleArtifactMap(
				production.articleIds.reduce(
					(acc, articleId) => ({
						...acc,
						[articleId]:
							artifacts
								.filter(({ article }) => article?.id === articleId)
								.sort((a, b) => b.created?.toDate?.() - a.created?.toDate?.())
								.map(({ id }) => id)[0] || null,
					}),
					{},
				),
			);
		}
	}, [artifacts, production.articleIds, production.articles]);

	const artifactMap = useMemo(() => new Map(artifacts?.map((artifact) => [artifact.id, artifact])), [artifacts]);

	const articleSegments =
		articles?.map((article) => {
			const contentTypes = Object.keys(getAcceptedArtifactFiles(article));

			return {
				article,
				artifact: artifactMap.get(articleArtifactMap[article.id]),
				acceptedArtifactIds:
					artifacts?.filter(({ contentType }) => contentTypes.includes(contentType)).map(({ id }) => id) ||
					[],
			};
		}) || [];

	const assignedArtifactIds = Object.values(articleArtifactMap).filter(Boolean);

	const unassignedArtifacts = artifacts?.filter(({ id }) => !assignedArtifactIds.includes(id)) || [];

	const moveArtifact = (artifactId, destinationId) => {
		// Update the articleArtifactMap with the new artifact assignment
		const newArticleArtifactMap = { ...articleArtifactMap };

		// Remove the artifact from every article it is assigned to, which also clears earlier duplicates
		for (const [articleId, assignedArtifactId] of Object.entries(newArticleArtifactMap)) {
			if (assignedArtifactId === artifactId) {
				newArticleArtifactMap[articleId] = null;
			}
		}

		// Assign the artifact to the destination article
		if (production.articleIds.includes(destinationId)) {
			newArticleArtifactMap[destinationId] = artifactId;
		}

		return updateArticlesMap(newArticleArtifactMap);
	};

	const hasArtifacts = artifacts?.length > 0;

	const isDirty = articleSegments?.some(({ artifact }) => artifact && !artifact.article);

	const totalDuration = secondsToDuration(
		artifacts
			?.filter((artifact) => assignedArtifactIds.includes(artifact.id))
			.reduce((acc, artifact) => acc + artifact.duration, 0),
		{ padHours: 2 },
	);

	return {
		isDirty,
		hasArtifacts,
		moveArtifact,
		articleSegments,
		unassignedArtifacts,
		totalDuration,
	};
}
